import { Link } from "react-router-dom";
import { Check, Ship } from "lucide-react";
import PageLayout from "../components/PageLayout";
import CtaBand from "../components/CtaBand";
import { Eyebrow, Reveal } from "../components/Reveal";
import { useContent } from "../content-context";

const SEO = {
  title: "Packages & Prices — Exclusive Private Island Weekend | Collanmore Island Lodge",
  description:
    "Exclusive-use private island weekend in Clew Bay, Westport, County Mayo — from €3,300 for two nights for up to 22 guests. Essential RIB ferry transfers, €50 island adventure package and optional extras.",
  path: "/private-island-packages-prices/",
};

export default function PackagesPage() {
  const { packages: pkg } = useContent();
  return (
    <PageLayout seo={SEO}>
      <section className="relative overflow-hidden pt-28 md:pt-36">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-5 pb-16 md:px-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <Eyebrow>{pkg.heroEyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="page-h1">
                {pkg.heroH1}
              </h1>
            </Reveal>
            {pkg.heroParas.map((p, i) => (
              <Reveal key={i} delay={0.14 + i * 0.06}>
                <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to="/contact/"
                data-testid="packages-hero-cta"
                className="mt-9 inline-flex items-center gap-3 bg-navy px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-navy"
              >
                {pkg.ctaLabel}
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="aspect-[4/3] overflow-hidden" data-testid="page-hero-image">
              <img src="/images/real/island-sunset.jpg" alt="The sun setting over Clew Bay from Collanmore Island" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-24 md:py-32" data-testid="package-one">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <Eyebrow light>{pkg.pkgEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-ivory md:text-5xl" data-testid="package-h2">
              {pkg.pkgH2}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-8 flex flex-wrap items-end gap-x-6 gap-y-2" data-testid="package-price">
              <span className="font-display text-6xl leading-none text-gold md:text-7xl">{pkg.pkgPrice}</span>
              <span className="pb-1 text-sm font-light text-ivory/70 md:text-base">{pkg.pkgPriceNote}</span>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-x-12 md:grid-cols-2" data-testid="package-includes">
            {pkg.pkgIncludes.map((inc, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <div className="flex items-start gap-4 border-b border-ivory/10 py-4 text-base font-light text-ivory/85">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-gold" />
                  {inc}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-gold/15 bg-navy-2 py-24 md:py-32" data-testid="package-two">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <Eyebrow light>{pkg.pkg2Eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-ivory md:text-5xl" data-testid="package2-h2">
              {pkg.pkg2H2}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-8 flex flex-wrap items-end gap-x-6 gap-y-3" data-testid="package2-price">
              <span className="font-display text-6xl leading-none text-gold md:text-7xl">{pkg.pkg2Price}</span>
              <span className="max-w-xl pb-1 text-sm font-light leading-relaxed text-ivory/70 md:text-base">{pkg.pkg2PriceNote}</span>
              <span className="mb-1 bg-gold px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy" data-testid="package2-badge">
                {pkg.pkg2Badge}
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-4 text-sm font-light text-ivory/60" data-testid="package2-note">{pkg.pkg2Note}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-x-12 md:grid-cols-2" data-testid="package2-includes">
            {pkg.pkg2Includes.map((inc, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <div className="flex items-start gap-4 border-b border-ivory/10 py-4 text-base font-light text-ivory/85">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-gold" />
                  {inc}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link
              to="/contact/"
              data-testid="package2-cta"
              className="mt-12 inline-flex items-center gap-3 bg-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-500 hover:bg-ivory"
            >
              {pkg.pkg2Cta}
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory pb-24 md:pb-32" data-testid="more-packages">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal>
            <div className="max-w-2xl">
              <Eyebrow>More Ways To Stay</Eyebrow>
              <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-navy md:text-5xl" data-testid="more-packages-heading">
                A package for every island occasion
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
            {[
              { n: 3, img: "/images/real/traitors-poster-midnight.webp", title: pkg.pkg3Title, price: pkg.pkg3Price, note: pkg.pkg3PriceNote, copy: pkg.pkg3Copy },
              { n: 4, img: "/images/real/clewbay-aerial.jpg", title: pkg.pkg4Title, price: pkg.pkg4Price, note: pkg.pkg4PriceNote, copy: pkg.pkg4Copy, cta: pkg.pkg4Cta },
              { n: 5, img: "/images/real/wedding-couple.jpg", title: pkg.pkg5Title, price: pkg.pkg5Price, note: pkg.pkg5PriceNote, copy: pkg.pkg5Copy },
              { n: 6, img: "/images/real/sumo-lawn.jpg", title: pkg.pkg6Title, price: pkg.pkg6Price, note: pkg.pkg6PriceNote, copy: pkg.pkg6Copy },
            ].map((c, i) => (
              <Reveal key={c.n} delay={(i % 2) * 0.08}>
                <article className="group flex h-full flex-col border border-navy/10 bg-white shadow-[0_8px_32px_rgba(10,25,47,0.08)]" data-testid={`package-card-${c.n}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={c.img} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-8 md:p-9">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Package {c.n} — {c.title}</span>
                    <h3 className="mt-3 font-display text-2xl leading-tight text-navy md:text-3xl" data-testid={`package-card-${c.n}-title`}>{c.title}</h3>
                    <p className="mt-4 font-display text-2xl text-gold md:text-3xl" data-testid={`package-card-${c.n}-price`}>{c.price}</p>
                    {c.note && <p className="mt-1 text-xs font-light text-navy/60 md:text-sm">{c.note}</p>}
                    <p className="mt-4 text-sm font-light leading-relaxed text-navy/75 md:text-base">{c.copy}</p>
                    <Link
                      to="/contact/"
                      data-testid={`package-card-${c.n}-cta`}
                      className="mt-auto inline-flex items-center justify-center gap-3 bg-navy px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-navy"
                    >
                      {c.cta || "Enquire Now"}
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-24" data-testid="ferry-transfers">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <div className="border border-gold/60 bg-white p-8 shadow-[0_8px_32px_rgba(10,25,47,0.08)] md:p-10">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h3 className="flex items-center gap-3 font-display text-2xl text-navy md:text-3xl">
                  <Ship className="h-6 w-6 text-gold" />
                  {pkg.ferryTitle}
                </h3>
                <span className="bg-navy px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory">Essential</span>
              </div>
              <p className="mt-5 font-display text-2xl text-gold md:text-3xl" data-testid="ferry-price">
                {pkg.ferryPrice}
              </p>
              <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-navy/80 md:text-base">{pkg.ferryNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory pb-24 md:pb-32" data-testid="adventure-package">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <div className="relative border-2 border-gold bg-navy-2 p-8 shadow-[0_16px_48px_rgba(10,25,47,0.25)] md:p-12">
              <span className="absolute -top-3.5 left-8 bg-gold px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy" data-testid="adventure-badge">
                {pkg.advBadge}
              </span>
              <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
                <div>
                  <h3 className="font-display text-3xl text-ivory md:text-4xl" data-testid="adventure-heading">
                    {pkg.advTitle}
                  </h3>
                  <div className="mt-6 flex flex-col gap-3" data-testid="adventure-includes">
                    {pkg.advIncludes.map((inc, i) => (
                      <div key={i} className="flex items-start gap-3 text-base font-light text-ivory/85">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-gold" />
                        {inc}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="md:text-right">
                  <p className="text-sm text-ivory/60 line-through">{pkg.advNormal}</p>
                  <p className="mt-1 font-display text-6xl leading-none text-gold md:text-7xl" data-testid="adventure-price">
                    {pkg.advPrice}
                  </p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory/70">{pkg.advUnit}</p>
                  <span className="mt-5 inline-block border border-gold/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-2" data-testid="adventure-saving">
                    {pkg.advSaving}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory pb-24 md:pb-32" data-testid="extras-booking">
        <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-10 px-5 md:px-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="h-full border border-navy/10 bg-white p-8 md:p-10" data-testid="extras-card">
              <h3 className="font-display text-2xl text-navy md:text-3xl">{pkg.extrasHeading}</h3>
              <div className="mt-6 flex flex-col">
                {pkg.extras.map((x, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-6 border-b border-navy/10 py-4 text-sm md:text-base">
                    <span className="font-light text-navy/85">{x.label}</span>
                    <span className="shrink-0 font-display text-lg text-navy" data-testid={`extra-price-${i + 1}`}>{x.price}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs font-light leading-relaxed text-navy/60" data-testid="extras-note">{pkg.extrasNote}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full bg-navy-2 p-8 md:p-10" data-testid="booking-card">
              <h3 className="font-display text-2xl text-ivory md:text-3xl">{pkg.bookingTitle}</h3>
              <div className="mt-6 flex flex-col gap-4" data-testid="booking-notes">
                {pkg.bookingNotes.map((note, i) => (
                  <p key={i} className={i === 0 ? "font-display text-2xl text-gold" : "text-sm font-light leading-relaxed text-ivory/80 md:text-base"}>
                    {note}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand heading={pkg.ctaHeading} lead={pkg.ctaLead} cta={pkg.ctaLabel} to="/contact/" />
    </PageLayout>
  );
}
